import React, { useEffect, useRef, useMemo } from 'react'
import { BarChart } from '@mui/x-charts/BarChart'
import './Bar.css'

const Bar = ({ 
  data, 
  series,
  xKey = 'x', 
  yKey = 'y', 
  xLabel, 
  yLabel, 
  title, 
  height = 200, 
  width = 400,
  widthContainer = '40%',
  heightContainer = '50%',
  colors = ['#15B79F', '#0E5F79', '#F2994A', '#6B7280', '#9333EA'],
  // controles de etiquetas eje X
  maxLabelLength = 12,
  tickLabelAngle = 90,
  tickFontSize = 7,
  // muestra el eje Y en porcentaje
  yAsPercent = false,
  yPercentFromFraction = false,
  // normaliza (solo para mostrar) contra el valor máximo global de las series
  yPercentNormalizeToMax = false,
  // decimales a mostrar cuando el eje Y es en porcentaje
  yPercentDecimals = 0,
  // Unidades del item
  yUnit = '',
  customTooltip = false,
  tooltipLabelKey = 'x',
  tooltipValueKey = 'y',
  tooltipValueLabel = 'Value'
}) => {
  const containerRef = useRef(null);

  const truncate = (s, n) => {
    const str = String(s ?? '')
    return str.length > n ? str.slice(0, n) + '…' : str
  }

  const originalDataMap = useMemo(() => {
    const map = new Map();
    if (Array.isArray(series) && series.length > 0 && series[0]?.data) {
      series[0].data.forEach(item => {
        const xv = (item && typeof item === 'object') ? (item[xKey] ?? item.x ?? '') : '';
        if (xv !== '') {
          map.set(xv, item);
        }
      });
    } else if (Array.isArray(data)) {
      data.forEach(item => {
        const xv = (item && typeof item === 'object') ? (item[xKey] ?? item.x ?? '') : '';
        if (xv !== '') {
          map.set(xv, item);
        }
      });
    }
    return map;
  }, [series, data, xKey]);

  // unión de todas las series, y datos alineados a ese dominio
  const { xCategories, alignedSeries } = useMemo(() => {
    // Caso: múltiples series
    if (Array.isArray(series) && series.length > 0) {
      const xSet = new Set();
      series.forEach(s => {
        (s?.data || []).forEach(item => {
          const xv = (item && typeof item === 'object') ? (item[xKey] ?? item.x ?? '') : '';
          if (xv !== '') xSet.add(xv);
        });
      });
      const xCats = Array.from(xSet);

      const aligned = series.map((s, idx) => {
        const map = new Map(
          (s?.data || []).map(item => {
            const xv = (item && typeof item === 'object') ? (item[xKey] ?? item.x ?? '') : '';
            const yv = (item && typeof item === 'object') ? (item[yKey] ?? item.y ?? 0) : 0;
            return [xv, yv];
          })
        );
        return {
          label: s?.label || s?.name || `Serie ${idx + 1}`,
          data: xCats.map(x => Number(map.get(x) ?? 0)),
          color: s?.color || colors[idx % colors.length],
          valueFormatter: (v) => `${Number(v ?? 0).toLocaleString()} ${yUnit}`,
        };
      });

      return { xCategories: xCats, alignedSeries: aligned };
    }

    // Caso: una sola serie
    if (Array.isArray(data) && data.length > 0) {
      const xCats = data.map(item =>
        (item && typeof item === 'object') ? (item[xKey] ?? item.x ?? '') : ''
      );
      const values = data.map(item =>
        (item && typeof item === 'object') ? (item[yKey] ?? item.y ?? 0) : 0
      );
      return {
        xCategories: xCats,
        alignedSeries: [{ data: values, color: colors[0], label: '' }],
      };
    }

    // Sin datos
    return { xCategories: [], alignedSeries: [{ data: [], color: colors[0], label: '' }] };
  }, [series, data, xKey, yKey, colors]);

  // Tooltip nativo en ticks del eje X
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const nodes = root.querySelectorAll('.MuiChartsAxis-tickLabel');
    Array.from(nodes).forEach((node, i) => {
      const prev = node.querySelector('title');
      if (prev) prev.remove();
      const titleEl = document.createElementNS('http://www.w3.org/2000/svg', 'title');
      titleEl.textContent = String(xCategories[i] ?? '');
      node.appendChild(titleEl);
      node.style.pointerEvents = 'all';
    });
  }, [xCategories, height, width, maxLabelLength]);

  const hasAnyCategory = xCategories.length > 0;

  // máximo global de las series para normalización opcional
  const maxValue = useMemo(() => {
    let all = [];
    alignedSeries.forEach(s => { all = all.concat(s.data) });
    const m = all.length ? Math.max(...all) : 0;
    return Number.isFinite(m) && m > 0 ? m : 1;
  }, [alignedSeries]);

  // formateador de valores del eje Y (con % opcional)
  const yTickFormatter = (v) => {
    const val = Number(v);
    if (!Number.isFinite(val)) return String(v);
    if (!yAsPercent) return String(val);

    // Prioridad: fracción 0–1 → porcentaje directo
    if (yPercentFromFraction) {
      return `${(val * 100).toFixed(yPercentDecimals)}%`;
    }

    // Si se solicita normalizar contra el máximo global
    if (yPercentNormalizeToMax) {
      const frac = (val / maxValue) * 100;
      return `${frac.toFixed(yPercentDecimals)}%`;
    }

    // Por defecto: tratar el valor como 0..100
    return `${val.toFixed(yPercentDecimals)}%`;
  };

  return (
    <div className="bar-chart-container" style={{ height: heightContainer, width: widthContainer }}>
      {title && <h3 className="chart-title">{title}</h3>}
      <div className="chart-wrapper" ref={containerRef}>
        {hasAnyCategory ? (
          <BarChart
            xAxis={[{
              scaleType: 'band',
              data: xCategories,
              label: xLabel || '',
              valueFormatter: (v, context) =>
                context?.location === 'tick' ? truncate(String(v), maxLabelLength) : String(v),
              tickLabelStyle: { angle: tickLabelAngle, fontSize: tickFontSize },
              // tickLabelInterval: () => true, // fuerza todos los ticks
            }]}
            yAxis={[{
              label: yLabel || '',
              tickLabelStyle: { fontSize: tickFontSize * 1.5 },
              valueFormatter: yTickFormatter,
            }]}
            series={alignedSeries}
            grid={{ vertical: true, horizontal: true }}
            height={height}
            width={width}
            margin={{
              left: 8,
              right: 8,
              top: Math.max(24, tickFontSize * 2),
              bottom: Math.max(36, tickFontSize * 3),
            }}
          />
        ) : (
          <div className="no-data-message">no data available</div>
        )}
      </div>
    </div>
  )
}

export default Bar
